import {RxdbRepository} from "../../rxdb.repository";
import {Page} from "../../../utils/page";
import {deepCloneListWithPrototype} from "../../../utils/clone-utils";
import * as uuid from 'uuid';
import {cloneDeep} from 'lodash-es';
import {Opportunity} from 'src/app/core/models/opportunity/opportunity.model';
import {QuoteSummary} from 'src/app/core/models/quote/quote-summary';
import {from, Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';

export class RxdbOfflineEquoteOpportunityRepository {

    private collectionName: string;

    constructor(private readonly rxdbRepository: RxdbRepository) {
        this.collectionName = 'equote_opportunities';
    }

    async getOpportunitiesWithQuotes(pageable: Map<string, string>, searchParam: Map<string, string>): Promise<Page<Opportunity>> {


        const page = pageable.has('page') ? parseInt(pageable.get('page')!) : 0;
        const size = pageable.has('size') ? parseInt(pageable.get('size')!) : 20;

        const selector: any = { $and: [] };

        let db = await this.rxdbRepository.initDb();

        const statusFilter = searchParam.get('status');
        if (statusFilter) {
            selector.$and.push({ status: { $eq: statusFilter } });
        }


        const labelFilter = searchParam.get('opportunityLabel');
        if (labelFilter) {
            selector.$and.push({ label: { $regex: labelFilter, $options: 'i' } });
        }

        const sortParam: string = pageable.get('sort') ?? '';
        const sortOrder: 'asc' | 'desc' = sortParam.endsWith(',asc') ? 'asc' : 'desc';
        const sortField = sortParam.startsWith('modificationDate') ? 'modificationDate' : 'creationDate';

        const pagedDocs = await db['equote_opportunities']
            .find({
                selector: selector,
                sort: [{ [sortField]: sortOrder }],
                skip: page * size,
                limit: size
            })
            .exec();

        const totalDocumentsCount = await db['equote_opportunities']
            .count({ selector: selector })
            .exec();

        //  récupérer les quotes pour CHAQUE opportunity
        const opportunities =  deepCloneListWithPrototype(pagedDocs.map(d => d._data) || [], Opportunity);

        for (const opportunity of opportunities) {

            const quotes = await db['quotes']
                .find({
                    selector: {
                        opportunityOfflineId: { $eq: opportunity.offlineId }
                    },
                    sort: [{ creationDate: 'desc' }]
                })
                .exec();
            opportunity.quotes = deepCloneListWithPrototype(quotes.map(q => q._data).map(q => new QuoteSummary(
                q.offlineId,
                q.reference,
                q.label,
                q.totalMonthlyAmount,
                q.totalWeeklyAmount,
                q.clientInfos,
                q.status,
                q.creationDate,
                q.cmcProtocolCode,
                q.cmcProtocolName,
                q.currency,
                q.color,
                q.offlineId
            )) || [], QuoteSummary);

        }

        return {
            totalElements: totalDocumentsCount,
            totalPages: Math.ceil(totalDocumentsCount / size),
            first: page === 0,
            last: (page + 1) * size >= totalDocumentsCount,
            size,
            content: opportunities,
            number: page,
            numberOfElements: opportunities.length,
            empty: opportunities.length === 0
        };
    }


    emptyPage(size: number, page: number): Page<Opportunity> {
        return {
            totalElements: 0,
            totalPages: 0,
            first: true,
            last: true,
            size,
            content: [],
            number: page,
            numberOfElements: 0,
            empty: true
        };
    }


    async createOpportunity(opportunity: Opportunity): Promise<any> {

        let _uuid = uuid.v4();

        let creationDate = new Date().toISOString()

        let serializedOpportunity = {
            ...opportunity,
            _id: _uuid,
            isSynchronized: false,
            offlineId: _uuid,
            creationDate: creationDate,
            modificationDate: creationDate,
            status: opportunity.status ?? null,
            salesFileStatus: opportunity.salesFileStatus ?? null,
            mainQuoteOfflineId: opportunity.mainQuoteOfflineId,
            employee: opportunity.employee ?? null
        };

        let db = await this.rxdbRepository.initDb()
        const opp = await db[this.collectionName].insert(serializedOpportunity);
        return cloneDeep(opp._data);
    }

    watchOpportunityChanges(): Observable<any> {
        return from(this.rxdbRepository.initDb()).pipe(
            switchMap(db => db['equote_opportunities'].$)
        );
    }

    async getOpportunityByOfflineId(offlineId: string): Promise<Opportunity> {
        const db = await this.rxdbRepository.initDb();
        const doc = await db['equote_opportunities'].findOne({
            selector: { offlineId: { $eq: offlineId } }
        }).exec();
        return cloneDeep(doc._data) as Opportunity;
    }

    async updateOpportunity(opportunity: Opportunity): Promise<any> {
        let db = await this.rxdbRepository.initDb()
        const existingOpp = cloneDeep( await db[this.collectionName].findOne({
            selector: {
                offlineId: {$eq: opportunity.offlineId}
            }
        }).exec());
        let serializedOpportunity = {
            ...existingOpp._data,
            isSynchronized: false,
            modificationDate: new Date().toISOString(),
            status: opportunity.status ?? null,
            salesFileStatus: opportunity.salesFileStatus ?? null,
            mainQuoteOfflineId: opportunity.mainQuoteOfflineId,
            employee: opportunity.employee ?? existingOpp._data.employee ?? null
        };
        const opp = await db[this.collectionName].upsert(serializedOpportunity);
        return cloneDeep(opp._data);
    }

}
